import React, { useState, useEffect, useCallback } from "react";
import { FaPen, FaTrash, FaTimes } from "react-icons/fa";
import "./AdminAttendance.css";
import Header from "../layouts/Employee/Header";
import SubHeader from "../layouts/Employee/SubHeader";


const AdminAttendance = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [departmentFilter, setDepartmentFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");

 
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

 
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);

  // Load sample data
  useEffect(() => {
    const sampleData = [
      {
        id: 1,
        name: "John Doe",
        department: "Human Resources",
        date: "2025-11-07",
        timeIn: "08:00",
        timeOut: "17:00",
        totalHours: "9h 0m",
        status: "Present",
      },
      {
        id: 2,
        name: "Jane Smith",
        department: "Finance",
        date: "2025-11-07",
        timeIn: "08:30",
        timeOut: "16:45",
        totalHours: "8h 15m",
        status: "Late",
      },
      {
        id: 3,
        name: "Mark Lee",
        department: "IT",
        date: "2025-11-07",
        timeIn: "",
        timeOut: "",
        totalHours: "0h 0m",
        status: "Absent",
      },
    ];
    setAttendanceData(sampleData);
    setFilteredData(sampleData);
  }, []);

  // Filtering and sorting
  useEffect(() => {
    let data = [...attendanceData];
    if (searchTerm)
      data = data.filter((r) =>
        r.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    if (statusFilter !== "All")
      data = data.filter((r) => r.status === statusFilter);
    if (departmentFilter !== "All")
      data = data.filter((r) => r.department === departmentFilter);
    if (dateFilter) data = data.filter((r) => r.date === dateFilter);

    data.sort((a, b) => {
      const aVal = (a[sortBy] || "").toString().toLowerCase();
      const bVal = (b[sortBy] || "").toString().toLowerCase();
      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredData(data);
  }, [
    searchTerm,
    statusFilter,
    departmentFilter,
    dateFilter,
    sortBy,
    sortOrder,
    attendanceData,
  ]);

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("All");
    setDepartmentFilter("All");
    setDateFilter("");
    setSortBy("name");
    setSortOrder("asc");
  };

  // Auto calculate total hours
  const calculateTotalHours = (timeIn, timeOut) => {
    if (!timeIn || !timeOut) return "0h 0m";
    const [inH, inM] = timeIn.split(":").map(Number);
    const [outH, outM] = timeOut.split(":").map(Number);
    let diffMins = outH * 60 + outM - (inH * 60 + inM);
    if (diffMins < 0) diffMins += 24 * 60;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  // --- Edit Modal logic ---
  const openEditModal = (record) => {
    setEditRecord({ ...record });
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditRecord(null);
  };
  const handleSaveEdit = () => {
    const totalHours = calculateTotalHours(
      editRecord.timeIn,
      editRecord.timeOut
    );
    setAttendanceData((prev) =>
      prev.map((item) =>
        item.id === editRecord.id ? { ...editRecord, totalHours } : item
      )
    );
    closeEditModal();
  };

  // --- Delete Modal logic ---
  const openDeleteModal = (record) => {
    setDeleteRecord(record);
    setIsDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteRecord(null);
  };
  const confirmDelete = () => {
    setAttendanceData((prev) => prev.filter((r) => r.id !== deleteRecord.id));
    closeDeleteModal();
  };

  // --- Keyboard shortcuts (Esc closes, Enter saves) ---
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        if (isEditModalOpen) closeEditModal();
        if (isDeleteModalOpen) closeDeleteModal();
      }
      if (e.key === "Enter" && isEditModalOpen) handleSaveEdit();
    },
    [isEditModalOpen, isDeleteModalOpen, handleSaveEdit]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="admin-attendance-container">
      <Header />
      <SubHeader 
        style ={{marginTop: '48px'}}
      />
      <h2 className="attendance-title">Employee Attendance Records</h2>

      <div className="filter-section">
        <div className="filter-item search-bar">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search employee name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-item">
          <label>Department:</label>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Human Resources">Human Resources</option>
            <option value="Finance">Finance</option>
            <option value="IT">IT</option>
          </select>
        </div>

        <div className="filter-item">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All</option>
            <option value="Present">Present</option>
            <option value="Late">Late</option>
            <option value="Absent">Absent</option>
          </select>
        </div>

        <div className="filter-item">
          <label>Date:</label>
          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          />
        </div>

        <div className="filter-item clear-btn">
          <button onClick={handleClearFilters}>Clear Filters</button>
        </div>
      </div>

      <div className="attendance-table-wrapper">
        <table className="attendance-table">
          <thead>
            <tr>
              <th>Employee Name</th>
              <th>Department</th>
              <th>Date</th>
              <th>Time In</th>
              <th>Time Out</th>
              <th>Total Hours</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((record) => (
                <tr key={record.id}>
                  <td>{record.name}</td>
                  <td>{record.department}</td>
                  <td>{record.date}</td>
                  <td>{record.timeIn || "-"}</td>
                  <td>{record.timeOut || "-"}</td>
                  <td>{record.totalHours}</td>
                  <td className={`status ${record.status.toLowerCase()}`}>
                    {record.status}
                  </td>
                  <td>
                    <div className="action-icons">
                      <FaPen
                        className="edit-icon"
                        onClick={() => openEditModal(record)}
                      />
                      <FaTrash
                        className="delete-icon"
                        onClick={() => openDeleteModal(record)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="no-data">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isEditModalOpen && editRecord && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => e.target === e.currentTarget && closeEditModal()}
        >
          <div className="modal-content fade-in">
            <div className="modal-header">
              <h3>Edit Attendance</h3>
              <FaTimes className="close-icon" onClick={closeEditModal} />
            </div>
            <div className="modal-body">
              <label>Employee:</label>
              <input type="text" value={editRecord.name} disabled readOnly />

              <label>Date:</label>
              <input
                type="date"
                value={editRecord.date}
                onChange={(e) =>
                  setEditRecord({ ...editRecord, date: e.target.value })
                }
              />

              <label>Time In:</label>
              <input
                type="time"
                value={editRecord.timeIn}
                onChange={(e) =>
                  setEditRecord({ ...editRecord, timeIn: e.target.value })
                }
              />

              <label>Time Out:</label>
              <input
                type="time"
                value={editRecord.timeOut}
                onChange={(e) =>
                  setEditRecord({ ...editRecord, timeOut: e.target.value })
                }
              />

              <label>Status:</label>
              <select
                value={editRecord.status}
                onChange={(e) =>
                  setEditRecord({ ...editRecord, status: e.target.value })
                }
              >
                <option value="Present">Present</option>
                <option value="Late">Late</option>
                <option value="Absent">Absent</option>
              </select>
            </div>
            <div className="modal-footer">
              <button className="save-btn" onClick={handleSaveEdit}>
                Save Changes
              </button>
              <button className="cancel-btn" onClick={closeEditModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteModalOpen && deleteRecord && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => e.target === e.currentTarget && closeDeleteModal()}
        >
          <div className="modal-content fade-in">
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <FaTimes className="close-icon" onClick={closeDeleteModal} />
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the attendance record for{" "}
                <strong>{deleteRecord.name}</strong> on{" "}
                <strong>{deleteRecord.date}</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button className="delete-btn" onClick={confirmDelete}>
                Delete
              </button>
              <button className="cancel-btn" onClick={closeDeleteModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAttendance;
